import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Mail, Phone, MapPin, CreditCard, Loader2 } from 'lucide-react';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import { useClinic, useUpdateClinicSettings, type ClinicRow } from '@/hooks/useClinicSettings';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import type { Json } from '@/types/database.generated';

type ClinicType = ClinicRow['type'];

interface AddressShape {
  street: string;
  zip: string;
  city: string;
  country: string;
}

const EMPTY_ADDRESS: AddressShape = { street: '', zip: '', city: '', country: 'Danmark' };

function readAddress(value: Json): AddressShape {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const v = value as Record<string, Json>;
    return {
      street: typeof v.street === 'string' ? v.street : '',
      zip: typeof v.zip === 'string' ? v.zip : '',
      city: typeof v.city === 'string' ? v.city : '',
      country: typeof v.country === 'string' ? v.country : 'Danmark',
    };
  }
  return EMPTY_ADDRESS;
}

const CLINIC_TYPES: ClinicType[] = ['psychologist', 'dentist', 'specialist', 'emdr_therapist'];

export function ClinicGeneral() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { hasPermission } = useAuth();
  const { data: clinic, isLoading, error } = useClinic(activeClinicId);
  const update = useUpdateClinicSettings(activeClinicId);

  const canEdit = Boolean(
    activeClinicId && hasPermission(activeClinicId, 'settings_general', 'update'),
  );

  const [name, setName] = useState('');
  const [clinicType, setClinicType] = useState<ClinicType>('psychologist');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [cvr, setCvr] = useState('');
  const [address, setAddress] = useState<AddressShape>(EMPTY_ADDRESS);
  const [questionnaireDefaultLocale, setQuestionnaireDefaultLocale] = useState<'da-DK' | 'en-DK'>('da-DK');

  useEffect(() => {
    if (!clinic) return;
    setName(clinic.name);
    setClinicType(clinic.type);
    setEmail(clinic.email ?? '');
    setPhone(clinic.phone ?? '');
    setCvr(clinic.cvr ?? '');
    setAddress(readAddress(clinic.address));
    setQuestionnaireDefaultLocale(((clinic as Record<string, unknown>).questionnaire_default_locale as 'da-DK' | 'en-DK') ?? 'da-DK');
  }, [clinic]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }
  if (error) return <ErrorAlert message={t('settings.load_error')} />;
  if (!clinic) return <ErrorAlert message={t('settings.no_clinic')} />;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await update.mutateAsync({
        name,
        type: clinicType,
        email,
        phone,
        cvr,
        address: address as unknown as Json,
        questionnaire_default_locale: questionnaireDefaultLocale,
      });
      toast.success(t('settings.saved_toast'));
    } catch (err) {
      console.error('update_clinic_settings failed', err);
      toast.error(t('settings.save_error_toast'));
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <SectionCard
        title={t('settings.general.title')}
        subtitle={t('settings.general.subtitle')}
        readOnly={!canEdit}
      >
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <FieldWithIcon
            id="clinic-name"
            label={t('settings.general.clinic_name')}
            icon={Building2}
            value={name}
            onChange={setName}
            disabled={!canEdit}
          />
          <div>
            <label
              htmlFor="clinic-type"
              className="block text-sm font-medium text-gray-700 dark:text-gray-200"
            >
              {t('settings.general.clinic_type')}
            </label>
            <select
              id="clinic-type"
              value={clinicType}
              onChange={(e) => setClinicType(e.target.value as ClinicType)}
              disabled={!canEdit}
              className="mt-1 block h-[44px] w-full rounded-xl border border-gray-300 bg-white px-3 text-gray-900 focus:border-indigo-500 focus:ring-indigo-500 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            >
              {CLINIC_TYPES.map((tp) => (
                <option key={tp} value={tp}>
                  {t(`settings.general.clinic_type_${tp}`)}
                </option>
              ))}
            </select>
          </div>

          <FieldWithIcon
            id="clinic-email"
            label={t('settings.general.email')}
            icon={Mail}
            type="email"
            value={email}
            onChange={setEmail}
            disabled={!canEdit}
          />
          <FieldWithIcon
            id="clinic-phone"
            label={t('settings.general.phone')}
            icon={Phone}
            type="tel"
            value={phone}
            onChange={setPhone}
            disabled={!canEdit}
          />
          <FieldWithIcon
            id="clinic-cvr"
            label={t('settings.general.cvr')}
            icon={CreditCard}
            value={cvr}
            onChange={setCvr}
            disabled={!canEdit}
          />
        </div>
      </SectionCard>

      <SectionCard
        title={t('settings.general.questionnaires_section', 'Spørgeskemaer')}
        subtitle={t('settings.general.questionnaires_subtitle', 'Standard sprogindstillinger for spørgeskema-invitationer')}
        readOnly={!canEdit}
      >
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label
              htmlFor="questionnaire-locale"
              className="block text-sm font-medium text-gray-700 dark:text-gray-200"
            >
              {t('settings.general.questionnaire_default_locale', 'Standard sprog for spørgeskemaer')}
            </label>
            <select
              id="questionnaire-locale"
              value={questionnaireDefaultLocale}
              onChange={(e) => setQuestionnaireDefaultLocale(e.target.value as 'da-DK' | 'en-DK')}
              disabled={!canEdit}
              className="mt-1 block h-[44px] w-full rounded-xl border border-gray-300 bg-white px-3 text-gray-900 focus:border-indigo-500 focus:ring-indigo-500 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            >
              <option value="da-DK">Dansk (da-DK)</option>
              <option value="en-DK">Engelsk (en-DK)</option>
            </select>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {t('settings.general.questionnaire_default_locale_hint', 'Gælder for patienter uden eksplicit sprogpræference')}
            </p>
          </div>
        </div>
      </SectionCard>

      <SectionCard title={t('settings.general.address_section')} readOnly={!canEdit}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <FieldWithIcon
              id="addr-street"
              label={t('settings.general.address_street')}
              icon={MapPin}
              value={address.street}
              onChange={(v) => setAddress((a) => ({ ...a, street: v }))}
              disabled={!canEdit}
            />
          </div>
          <PlainField
            id="addr-zip"
            label={t('settings.general.address_zip')}
            value={address.zip}
            onChange={(v) => setAddress((a) => ({ ...a, zip: v }))}
            disabled={!canEdit}
          />
          <PlainField
            id="addr-city"
            label={t('settings.general.address_city')}
            value={address.city}
            onChange={(v) => setAddress((a) => ({ ...a, city: v }))}
            disabled={!canEdit}
          />
          <PlainField
            id="addr-country"
            label={t('settings.general.address_country')}
            value={address.country}
            onChange={(v) => setAddress((a) => ({ ...a, country: v }))}
            disabled={!canEdit}
          />
        </div>
      </SectionCard>

      <SaveBar canEdit={canEdit} pending={update.isPending} />
    </form>
  );
}

// ---------------------------------------------------------------------------
// Local building blocks. Kept here (not in /components/ui) because they exist
// only for the settings forms and don't need cross-page reuse.
// ---------------------------------------------------------------------------

export function SectionCard({
  title,
  subtitle,
  readOnly,
  children,
}: {
  title: string;
  subtitle?: string;
  readOnly?: boolean;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-card-dark">
      <div className="mb-4">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white">{title}</h2>
        {subtitle && <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>}
        {readOnly && (
          <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
            {t('settings.read_only_hint')}
          </p>
        )}
      </div>
      {children}
    </section>
  );
}

export function SaveBar({ canEdit, pending }: { canEdit: boolean; pending: boolean }) {
  const { t } = useTranslation();
  if (!canEdit) return null;
  return (
    <div className="flex justify-end">
      <Button type="submit" disabled={pending}>
        {pending ? t('settings.saving') : t('settings.save')}
      </Button>
    </div>
  );
}

export function FieldWithIcon({
  id,
  label,
  icon: Icon,
  value,
  onChange,
  type = 'text',
  disabled,
}: {
  id: string;
  label: string;
  icon: typeof Building2;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  disabled?: boolean;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-200">
        {label}
      </label>
      <div className="relative mt-1 rounded-xl shadow-sm">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <Icon className="h-5 w-5 text-gray-400" />
        </div>
        <input
          id={id}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="block h-[44px] w-full rounded-xl border border-gray-300 bg-white pl-10 pr-3 text-gray-900 placeholder-gray-500 focus:border-indigo-500 focus:ring-indigo-500 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
        />
      </div>
    </div>
  );
}

export function PlainField({
  id,
  label,
  value,
  onChange,
  type = 'text',
  disabled,
  placeholder,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  disabled?: boolean;
  placeholder?: string;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-200">
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        className="mt-1 block h-[44px] w-full rounded-xl border border-gray-300 bg-white px-3 text-gray-900 placeholder-gray-500 focus:border-indigo-500 focus:ring-indigo-500 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
      />
    </div>
  );
}
